/**
 * @file WarehouseDraftForm.tsx
 * @description Форма создания/редактирования склада (phase19.13; phase19.31
 * зона «Склад» — поля, переключатели и кнопки кабинета, без капса).
 *
 * Вынесена из WmsWarehousesPage: страница держит список и запросы, форма —
 * только поля по контракту `value / onChange`. Поля адреса, телефона и графика
 * работы попадают клиенту (блок самовывоза на витрине, ЛК, письмо о готовности
 * заказа к выдаче), поэтому подписаны явно.
 *
 * phase19.14 (W8): «Тип склада» и «Доступен для продажи». Виды берутся из
 * реестра `WAREHOUSE_KIND_META` — новый вид добавляется записью, а не веткой.
 * У карантина и транзита флаг продажи снимается автоматически: так же делает
 * сервер (`resolveSellable`), и форма не должна показывать другое.
 *
 * Поля — не шире 720px (форма §5), переключатели — `Switch` вместо чекбокса.
 */

import { useId, type ReactNode } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { SelectField } from "../ui/SelectField";
import { SettingSwitchRow } from "./settings/SettingRows";
import { resolveSellable, WAREHOUSE_KINDS, WAREHOUSE_KIND_META, type WarehouseKind } from "../../lib/wms-warehouse-kind";

export interface WarehouseDraft {
  name: string;
  code: string;
  address: string;
  /** phase19 — телефон точки (блок самовывоза checkout). */
  phone: string;
  /** phase19.13 — график работы точки (ЛК клиента + письмо о готовности). */
  workingHours: string;
  /** phase19.14 (W8) — назначение склада. */
  kind: WarehouseKind;
  /** phase19.14 (W8) — участвует в «доступно» и в резерве. */
  isSellable: boolean;
  isDefault?: boolean;
}

interface Props {
  value: WarehouseDraft;
  onChange: (next: WarehouseDraft) => void;
  /** true — редактируем существующий склад (меняет заголовок и подпись кнопки). */
  editing: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

/** Поле с подписью и подсказкой под ним. */
function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint ? <p className="type-caption text-on-surface-variant">{hint}</p> : null}
    </div>
  );
}

export function WarehouseDraftForm({ value, onChange, editing, saving, onSave, onCancel }: Props) {
  const id = useId();
  const set = (patch: Partial<WarehouseDraft>) => onChange({ ...value, ...patch });
  const kindMeta = WAREHOUSE_KIND_META[value.kind];

  /** Смена вида пересчитывает флаг продажи — зеркало серверного правила. */
  function setKind(kind: WarehouseKind) {
    set({ kind, isSellable: resolveSellable(kind, value.isSellable) });
  }

  return (
    <section
      aria-labelledby={`${id}-title`}
      className="flex max-w-[45rem] flex-col gap-4 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)] lg:p-5"
    >
      <h2 id={`${id}-title`} className="type-section-title text-on-surface">
        {editing ? "Изменить склад" : "Новый склад"}
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field id={`${id}-name`} label="Название">
          <Input id={`${id}-name`} placeholder="Основной / ПВЗ Челябинск" value={value.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
        </Field>
        <Field id={`${id}-code`} label="Код">
          <Input id={`${id}-code`} placeholder="CHB-MAIN" value={value.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} className="font-mono" />
        </Field>
      </div>
      <Field id={`${id}-address`} label="Адрес (необязательно)">
        <Input id={`${id}-address`} placeholder="Челябинск, ул. Кирова, 5" value={value.address} onChange={(e) => set({ address: e.target.value })} />
      </Field>
      <Field id={`${id}-phone`} label="Телефон (необязательно)" hint="Показывается в блоке самовывоза на сайте и в письме клиенту.">
        <Input id={`${id}-phone`} type="tel" placeholder="+7 (351) 000-00-00" value={value.phone} onChange={(e) => set({ phone: e.target.value })} />
      </Field>
      {/* Свободный текст, сервер режет по 200 символам. */}
      <Field id={`${id}-hours`} label="График работы (необязательно)" hint="Показывается клиенту в личном кабинете и в письме о готовности заказа к выдаче.">
        <Input id={`${id}-hours`} maxLength={200} placeholder="Пн–Пт 10:00–19:00, Сб 11:00–16:00" value={value.workingHours} onChange={(e) => set({ workingHours: e.target.value })} />
      </Field>
      {/* Тип склада (W8) — от него зависит, попадёт ли остаток в «доступно». */}
      <Field id={`${id}-kind`} label="Тип склада" hint={kindMeta.hint}>
        <SelectField
          id={`${id}-kind`}
          value={value.kind}
          onChange={(next) => setKind(next as WarehouseKind)}
          options={WAREHOUSE_KINDS.map((k) => ({ value: k, label: WAREHOUSE_KIND_META[k].label }))}
          ariaLabel="Тип склада"
        />
      </Field>
      <SettingSwitchRow
        label="Доступен для продажи"
        hint={
          kindMeta.neverSellable
            ? `«${kindMeta.label}» в продажу не попадает — снято автоматически.`
            : "Остатки этого склада попадают в «доступно» и резервируются."
        }
        checked={value.isSellable}
        disabled={kindMeta.neverSellable}
        onChange={(isSellable) => set({ isSellable })}
      />
      <SettingSwitchRow
        label="Склад по умолчанию"
        hint="На него по умолчанию идут приёмки."
        checked={!!value.isDefault}
        onChange={(isDefault) => set({ isDefault })}
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={onSave} disabled={!value.name.trim() || !value.code.trim() || saving} loading={saving}>
          {editing ? "Сохранить" : "Создать"}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </section>
  );
}
